import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  resolveAccountIdFromLog,
  resolveTenantIdFromLog,
} from "../../../../helpers/activity_log_links";
import {
  ACTIVITY_LOG_ACTION_OPTIONS,
  ActivityLog,
  downloadActivityLogsCsv,
  getLogs,
} from "../../../../services/superAdmin/adminActivityLogService";
import { PaginationMeta } from "../../../../types/api";
import ActivityLogDetailModal from "./ActivityLogDetailModal";

const PER_PAGE = 15;

const SuperAdminActivityLogs = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const actionFilter = searchParams.get("action") ?? "";
  const tenantIdParam = searchParams.get("tenant_id") ?? "";
  const tenantIdFilter = /^\d+$/.test(tenantIdParam)
    ? Number(tenantIdParam)
    : undefined;

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const notAvailable = t("admin.activityLogs.common.notAvailable");
  const hasActiveFilter = Boolean(actionFilter || tenantIdFilter);

  useEffect(() => {
    document.title = t("admin.activityLogs.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    setPage(1);
  }, [actionFilter, tenantIdFilter]);

  const formatDateTime = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value)),
    [i18n.language]
  );

  const formatActionLabel = useCallback(
    (action: string) =>
      t(`admin.activityLogs.actionTypes.${action}`, {
        defaultValue: action,
      }),
    [t]
  );

  const updateFilter = useCallback(
    (key: "action" | "tenant_id", value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getLogs({
        page,
        per_page: PER_PAGE,
        ...(actionFilter ? { action: actionFilter } : {}),
        ...(tenantIdFilter ? { tenant_id: tenantIdFilter } : {}),
      });

      setLogs(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("admin.activityLogs.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, tenantIdFilter, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await downloadActivityLogsCsv({
        ...(actionFilter ? { action: actionFilter } : {}),
        ...(tenantIdFilter ? { tenant_id: tenantIdFilter } : {}),
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "activity-logs.csv";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success(t("admin.activityLogs.toast.exportSuccess"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.activityLogs.toast.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const openDetails = (log: ActivityLog) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const closeDetails = () => {
    setDetailModalOpen(false);
    setSelectedLog(null);
  };

  const columns = useMemo(
    () => [
      {
        header: t("admin.activityLogs.table.date"),
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell: any) => formatDateTime(cell.getValue()),
      },
      {
        header: t("admin.activityLogs.table.admin"),
        accessorKey: "super_admin",
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue()?.name ?? notAvailable,
      },
      {
        header: t("admin.activityLogs.table.user"),
        accessorKey: "user",
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue()?.name ?? notAvailable,
      },
      {
        header: t("admin.activityLogs.table.action"),
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const action = String(cell.getValue() ?? "");
          const tipId = `activity-action-${cell.row.original.id}`;

          return (
            <>
              <Badge
                id={tipId}
                color="soft-primary"
                className="fs-12"
                style={{ cursor: "help" }}
              >
                {formatActionLabel(action)}
              </Badge>
              <UncontrolledTooltip placement="top" target={tipId}>
                {t("admin.activityLogs.actionTooltip", { code: action })}
              </UncontrolledTooltip>
            </>
          );
        },
      },
      {
        header: t("admin.activityLogs.table.ip"),
        accessorKey: "ip_address",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="font-monospace">{cell.getValue()}</span>
        ),
      },
      {
        header: t("admin.activityLogs.table.actions"),
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const log = cell.row.original as ActivityLog;
          const tenantId = resolveTenantIdFromLog(log);
          const accountId = resolveAccountIdFromLog(log);

          return (
            <div className="d-flex flex-wrap gap-1">
              <Button
                color="soft-primary"
                size="sm"
                onClick={() => openDetails(log)}
              >
                <i className="ri-eye-line align-bottom me-1" />
                {t("admin.activityLogs.actions.details")}
              </Button>
              {tenantId != null ? (
                <Button
                  tag={Link}
                  to={`/super-admin/tenants?tenant_id=${tenantId}`}
                  color="soft-secondary"
                  size="sm"
                >
                  <i className="ri-building-line align-bottom me-1" />
                  {t("admin.activityLogs.actions.openTenant")}
                </Button>
              ) : null}
              {accountId != null ? (
                <Button
                  tag={Link}
                  to={`/super-admin/accounts?account_id=${accountId}`}
                  color="soft-info"
                  size="sm"
                >
                  <i className="ri-user-shared-line align-bottom me-1" />
                  {t("admin.activityLogs.actions.openAccount")}
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [t, formatDateTime, formatActionLabel, notAvailable]
  );

  const totalPages = meta?.last_page ?? 1;

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.activityLogs.breadcrumb.title")}
            pageTitle={t("admin.activityLogs.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="align-items-center g-3">
                    <Col lg={5}>
                      <h4 className="card-title mb-1">
                        {t("admin.activityLogs.card.title")}
                      </h4>
                      <p className="text-muted mb-0 small">
                        {t("admin.activityLogs.card.description")}
                      </p>
                    </Col>
                    <Col lg={7}>
                      <div className="d-flex flex-column flex-md-row gap-2 justify-content-lg-end align-items-stretch align-items-md-end">
                        <div style={{ minWidth: 220 }}>
                          <Label
                            htmlFor="activity-log-action-filter"
                            className="form-label mb-1"
                          >
                            {t("admin.activityLogs.filters.action.label")}
                          </Label>
                          <Input
                            id="activity-log-action-filter"
                            type="select"
                            value={actionFilter}
                            onChange={(event) =>
                              updateFilter("action", event.target.value)
                            }
                          >
                            <option value="">
                              {t("admin.activityLogs.filters.action.all")}
                            </option>
                            {ACTIVITY_LOG_ACTION_OPTIONS.map((action) => (
                              <option key={action} value={action}>
                                {formatActionLabel(action)}
                              </option>
                            ))}
                          </Input>
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <Label
                            htmlFor="activity-log-tenant-filter"
                            className="form-label mb-1"
                          >
                            {t("admin.activityLogs.filters.tenantId.label")}
                          </Label>
                          <Input
                            id="activity-log-tenant-filter"
                            type="number"
                            min={1}
                            value={tenantIdParam}
                            placeholder={t(
                              "admin.activityLogs.filters.tenantId.placeholder"
                            )}
                            onChange={(event) =>
                              updateFilter("tenant_id", event.target.value.trim())
                            }
                          />
                        </div>
                        <div className="d-flex gap-2">
                          {hasActiveFilter ? (
                            <Button
                              color="light"
                              onClick={clearFilters}
                              className="align-self-end"
                            >
                              {t("admin.activityLogs.filters.clear")}
                            </Button>
                          ) : null}
                          <Button
                            color="primary"
                            outline
                            className="align-self-end"
                            disabled={exporting || loading}
                            onClick={handleExport}
                          >
                            {exporting ? (
                              <Spinner size="sm" className="me-1" />
                            ) : (
                              <i className="ri-download-2-line align-bottom me-1" />
                            )}
                            {t("admin.activityLogs.actions.exportCsv")}
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  {tenantIdFilter ? (
                    <p className="text-muted small mt-3 mb-0">
                      {t("admin.activityLogs.filters.tenantId.activeHint", {
                        id: tenantIdFilter,
                      })}{" "}
                      <Link
                        to={`/super-admin/tenants?tenant_id=${tenantIdFilter}`}
                      >
                        {t("admin.activityLogs.filters.tenantId.openTenant")}
                      </Link>
                    </p>
                  ) : null}
                </CardHeader>

                <CardBody>
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-2 fw-medium">
                        {hasActiveFilter
                          ? t("admin.activityLogs.empty.noFilterResults")
                          : t("admin.activityLogs.empty.noData")}
                      </p>
                      <p className="mb-3 small">
                        {hasActiveFilter
                          ? t("admin.activityLogs.empty.filterHint")
                          : t("admin.activityLogs.empty.hint")}
                      </p>
                      {!hasActiveFilter ? (
                        <ul className="list-unstyled small mb-0 text-start d-inline-block">
                          <li className="mb-1">
                            • {t("admin.activityLogs.empty.examples.block")}
                          </li>
                          <li className="mb-1">
                            • {t("admin.activityLogs.empty.examples.plans")}
                          </li>
                          <li className="mb-1">
                            • {t("admin.activityLogs.empty.examples.courtesy")}
                          </li>
                          <li>
                            • {t("admin.activityLogs.empty.examples.impersonation")}
                          </li>
                        </ul>
                      ) : (
                        <Button color="soft-primary" size="sm" onClick={clearFilters}>
                          {t("admin.activityLogs.filters.clear")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="activity-logs-table">
                        <style>
                          {`
                            .activity-logs-table .row.align-items-center.mt-2 {
                              display: none !important;
                            }
                            .activity-log-json {
                              max-height: 240px;
                              overflow: auto;
                              white-space: pre-wrap;
                              word-break: break-word;
                            }
                          `}
                        </style>
                        <TableContainer
                          columns={columns}
                          data={logs}
                          customPageSize={logs.length || PER_PAGE}
                          tableClass="align-middle table-nowrap mb-0"
                          theadClass="table-light"
                        />
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("admin.activityLogs.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("admin.activityLogs.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("admin.activityLogs.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ActivityLogDetailModal
        isOpen={detailModalOpen}
        log={selectedLog}
        onClose={closeDetails}
        formatDateTime={formatDateTime}
        formatActionLabel={formatActionLabel}
      />
    </React.Fragment>
  );
};

export default SuperAdminActivityLogs;
